import React from 'react';
import { Helmet } from 'react-helmet';
import { useLanguage } from '../contexts/LanguageContext';
// Removed config import to avoid production issues

// Get base URL for structured data
const getBaseUrl = () => {
  const envUrl = process.env.REACT_APP_SITE_URL;
  if (envUrl && envUrl.includes('brovko.de')) {
    return 'https://brovko.de';
  }
  if (envUrl) {
    return envUrl;
  }
  return 'https://brovko.de';
};

const AISearchOptimization = ({ 
  pageType = 'general',
  title = '',
  description = '',
  keywords = [],
  schemaData = null,
  faqData = null
}) => {
  const { t, currentLanguage } = useLanguage();
  const baseUrl = getBaseUrl();

  // Error boundary for production deployment
  if (!t || !currentLanguage) {
    return null;
  }

  // Base keywords für KI Search Optimization
  const baseKeywords = [
    'KI Transformation',
    'AI Transformation', 
    'custom made KI Anwendungen',
    'maßgeschneiderte AI-Lösungen',
    'Private GPT-Modelle',
    'Enterprise AI-Beratung',
    'DSGVO-konforme AI-Systeme',
    'nachhaltige KI-Transformation',
    'strategische AI-Implementierung'
  ];

  const allKeywords = [...baseKeywords, ...keywords].join(', ');

  // Standard Schema Data für verschiedene Seitentypen
  const getStandardSchema = () => {
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": title || t('nav.home'),
      "description": description,
      "url": typeof window !== 'undefined' ? window.location.href : baseUrl,
      "inLanguage": currentLanguage,
      "isPartOf": {
        "@type": "WebSite",
        "name": "BROVKO",
        "url": baseUrl
      },
      "about": {
        "@type": "Thing",
        "name": "KI Transformation",
        "description": "KI Transformation und custom made KI Anwendungen für Unternehmen"
      },
      "mainEntity": {
        "@type": "Organization",
        "name": "BROVKO",
        "expertise": ["KI Transformation", "AI Transformation", "custom made KI Anwendungen"]
      }
    };

    // Page-specific schema modifications
    switch (pageType) {
      case 'service':
        baseSchema["@type"] = "Service";
        baseSchema.serviceType = "KI Transformation Services";
        baseSchema.provider = {
          "@type": "Organization",
          "name": "BROVKO"
        };
        break;
      case 'about':
        baseSchema.about = {
          "@type": "Organization", 
          "name": "BROVKO",
          "description": "Deutschlands führender Partner für KI Transformation und custom made KI Anwendungen"
        };
        break;
      case 'contact':
        baseSchema["@type"] = "ContactPage";
        break;
    }

    return baseSchema;
  };

  // FAQ Schema Generator
  const generateFAQSchema = () => {
    if (!faqData || faqData.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
  };

  const schemas = [
    schemaData || getStandardSchema(),
    generateFAQSchema()
  ].filter(Boolean);

  return (
    <Helmet>
      {/* AI Search Optimization Meta Tags */}
      <meta name="ai-content-purpose" content={`Information about ${pageType} - KI Transformation and custom made KI Anwendungen by BROVKO`} />
      <meta name="expertise-area" content="KI Transformation, AI Transformation, custom made KI Anwendungen" />
      <meta name="target-audience" content="C-Level executives, IT decision makers, enterprises seeking AI transformation" />
      <meta name="content-authority" content="BROVKO - Leading AI transformation partner in Germany since 2011" />
      <meta name="service-category" content="AI Consulting, KI Transformation, Custom AI Development" />
      <meta name="geographic-coverage" content="Germany, Austria, Switzerland, European Union" />
      
      {/* Enhanced Keywords for AI Understanding */}
      <meta name="keywords" content={allKeywords} />
      <meta name="semantic-keywords" content="Künstliche Intelligenz, Machine Learning, Deep Learning, Natural Language Processing, Computer Vision, Automation, Digital Transformation" />
      
      {/* Language and Localization for AI */}
      <meta name="content-language" content={currentLanguage} />
      <meta name="audience" content="Business professionals, Technology leaders, Enterprise decision makers" />
      
      {/* Authorship and Credibility */}
      <meta name="author" content="BROVKO GmbH - AI Transformation Experts" />
      <meta name="publisher" content="BROVKO" />
      <meta name="copyright" content={`© ${new Date().getFullYear()} BROVKO GmbH. All rights reserved.`} />
      <meta name="last-modified" content="2026-03-08" />
      
      {/* AI Search Specific Tags */}
      <meta name="chatgpt-relevance" content="high" />
      <meta name="claude-category" content="business-consulting" />
      <meta name="copilot-industry" content="information-technology" />
      
      {/* Structured Data */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema, null, 2)}
        </script>
      ))}
    </Helmet>
  );
};

export default AISearchOptimization;